import type { TemplateFormField } from '@common/types/templateFormFields';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { deleteFormField, updateTemplateFormProperties } from '@/services/TemplateFormsService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import { buildIntakeFormPropertiesPayload } from './intakeFormPayload';
import type { IntakeDesignData } from './types';

interface Props {
    currentGroupTitle: string;
    quoteTemplateId: number | null;
    intakeDesign: IntakeDesignData;
    setIntakeDesign: Dispatch<SetStateAction<IntakeDesignData>>;
    onClose: () => void;
    onDeleted?: () => void;
}

export default function DeleteGroupDialog({
    currentGroupTitle,
    quoteTemplateId,
    intakeDesign,
    setIntakeDesign,
    onClose,
    onDeleted,
}: Props) {
    const [isLoading, setIsLoading] = useState(false);

    const getFormGroupTitle = (field: TemplateFormField) =>
        field.questionSchema?.formGroupTitle ?? field.formGroupTitle ?? '';

    const onDelete = async () => {
        if (!currentGroupTitle) {
            errorSnackbar('No group selected');
            return;
        }

        const existingFormGroups = intakeDesign.formGroups ?? [];
        const updatedFormGroups = existingFormGroups.filter(
            (group) => group.name !== currentGroupTitle,
        );

        if (
            existingFormGroups.length > 0 &&
            updatedFormGroups.length === existingFormGroups.length
        ) {
            errorSnackbar('No group found for selected group');
            return;
        }

        const updatedTemplateFormFields = intakeDesign.templateFormFields.filter(
            (field) => getFormGroupTitle(field) !== currentGroupTitle,
        );

        const remainingGroupTitles =
            updatedFormGroups.length > 0
                ? [...updatedFormGroups]
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((group) => group.name)
                : updatedTemplateFormFields.reduce<string[]>((accumulator, field) => {
                      const groupTitle = getFormGroupTitle(field);
                      if (groupTitle && !accumulator.includes(groupTitle)) {
                          accumulator.push(groupTitle);
                      }
                      return accumulator;
                  }, []);

        const nextTabIndex =
            remainingGroupTitles.length === 0
                ? 0
                : Math.min(intakeDesign.currentIntakeTab.tab, remainingGroupTitles.length - 1);
        const nextTabLabel = remainingGroupTitles[nextTabIndex] ?? '';

        const updatedIntakeDesign = {
            ...intakeDesign,
            currentIntakeTab: {
                tab: nextTabIndex,
                tabLabel: nextTabLabel,
            },
            formGroups: updatedFormGroups,
            templateFormFields: updatedTemplateFormFields,
        };

        setIntakeDesign(updatedIntakeDesign);

        if (!quoteTemplateId) {
            successSnackbar('Group deleted successfully');
            onDeleted?.();
            onClose();
            return;
        }

        setIsLoading(true);
        try {
            const fieldsToDelete = intakeDesign.templateFormFields
                .filter((field) => getFormGroupTitle(field) === currentGroupTitle)
                .sort((a, b) => {
                    const aConditional = a.questionSchema?.isConditionalQuestion ? 0 : 1;
                    const bConditional = b.questionSchema?.isConditionalQuestion ? 0 : 1;
                    return aConditional - bConditional;
                });

            for (const field of fieldsToDelete) {
                if (field.templateFormFieldId) {
                    await deleteFormField(quoteTemplateId, field.templateFormFieldId);
                }
            }

            if (intakeDesign.templateFormId) {
                await updateTemplateFormProperties(
                    quoteTemplateId,
                    intakeDesign.templateFormId,
                    // Groups can only be edited on V2 forms
                    buildIntakeFormPropertiesPayload({
                        intakeDesign,
                        isIntakeFormV2: true,
                        formGroups: updatedFormGroups,
                    }),
                );
            }

            successSnackbar('Group deleted successfully');
            onDeleted?.();
            onClose();
        } catch (error: any) {
            setIntakeDesign(intakeDesign);
            console.error(error);
            errorSnackbar(error.response?.data || 'Something went wrong!');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Delete group</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete the "{currentGroupTitle}" group? This will
                    remove all template form fields in this group.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button color="error" variant="contained" onClick={onDelete} disabled={isLoading}>
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}
